(function () {
  'use strict';

  // The official Kaching React component accepts these same JSON payloads.
  // Keep selection data server-owned; this parser never executes app scripts,
  // fetches app data, calculates discounts, or modifies an app configuration.
  const maisonId = (value) => value == null ? '' : String(value);
  const maisonObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const maisonInteger = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const maisonError = (reason) => Object.assign(new Error(`Maison Kaching data: ${reason}`), {
    name: 'MaisonKachingDataError', code: 'MAISON_KACHING_UNSUPPORTED'
  });
  const maisonRequire = (condition, reason) => { if (!condition) throw maisonError(reason); };
  const maisonEmpty = (value) => value == null || Array.isArray(value) && value.length === 0;

  function maisonJSON(node) {
    maisonRequire(node && node.getAttribute('type')?.toLowerCase() === 'application/json', 'Missing JSON app data');
    try { return JSON.parse(node.textContent); } catch (_) { throw maisonError('Malformed JSON app data'); }
  }

  function maisonOne(document, selector) {
    const nodes = document.querySelectorAll(selector);
    maisonRequire(nodes.length === 1, 'Ambiguous or missing app configuration');
    return maisonJSON(nodes[0]);
  }

  function maisonVisible(deal, product) {
    const includes = (values, id) => Array.isArray(values) && values.some((value) => maisonId(value) === maisonId(id));
    const collections = product.collectionIds || [];
    switch (deal.blockVisibility) {
      case 'selected-products': return includes(deal.selectedProductIds, product.id);
      case 'selected-collections': return collections.some((id) => includes(deal.selectedCollectionIds, id));
      case 'excluded-products': return !includes(deal.excludedProductIds, product.id)
        && !collections.some((id) => includes(deal.excludedCollectionIds, id));
      case 'all-products': return true;
      default: throw maisonError('Unknown offer visibility');
    }
  }

  function maisonSimpleOffer(deal, product) {
    maisonRequire(Array.isArray(deal.dealBars) && deal.dealBars.length > 0 && maisonId(deal.id), 'Missing offer bars');
    // These features need extra products, media, consent/tracking, or a cart
    // transport beyond a single native line. Let the native embed own them.
    const unsupported = ['collectionBreaksEnabled', 'subscriptionsEnabled', 'progressiveGiftsEnabled',
      'checkboxUpsellsEnabled', 'scratchOffEnabled', 'timerEnabled', 'stickyAtcEnabled', 'skipCart',
      'customStylesEnabled', 'updateNativePrice', 'showTotalPriceInAddToCartButton'];
    maisonRequire(!unsupported.some((key) => deal[key] === true), 'Offer requires the native integration');
    maisonRequire(maisonEmpty(deal.defaultVariantsV2) && maisonEmpty(deal.otherProductHandles), 'Offer has custom product selections');
    maisonRequire(product.variants.length === 1 || deal.differentVariantsEnabled !== true, 'Offer can generate different variant lines');
    const ids = new Set();
    for (const bar of deal.dealBars) {
      maisonRequire(maisonObject(bar) && maisonId(bar.id) && !ids.has(maisonId(bar.id)), 'Invalid offer bar identity');
      ids.add(maisonId(bar.id));
      maisonRequire(!bar.dealBarType || bar.dealBarType === 'quantity-break', 'Offer is not a simple quantity break');
      maisonRequire(maisonInteger(bar.quantity), 'Invalid offer quantity');
      maisonRequire(['freeGifts', 'upsells', 'multipleGiftsSelectors', 'featuredImages'].every((key) => maisonEmpty(bar[key])),
        'Offer has extra products or images');
      maisonRequire(['productPersonalisation', 'mediaImageGID', 'badgeImageGID', 'featuredMediaGID', 'featuredProductGID',
        'sellingPlanGid'].every((key) => !bar[key]), 'Offer has unsupported product configuration');
      maisonRequire(!bar.sellingPlanEnabled && !bar.hideBehindScratchOff && !bar.requireVariantSelectionEnabled,
        'Offer requires additional selection');
    }
    maisonRequire(!deal.preselectedDealBarId || ids.has(maisonId(deal.preselectedDealBarId)), 'Invalid default offer selection');
  }

  function maisonRead(document, context) {
    maisonRequire(document?.querySelectorAll && maisonObject(context?.product), 'Missing target context');
    const config = maisonOne(document, 'script#kaching-bundles-config');
    maisonRequire(maisonObject(config) && maisonObject(config.featureFlags), 'Invalid app configuration');
    maisonRequire(maisonId(config.productId) === maisonId(context.product.id), 'App product context mismatch');
    maisonRequire(config.locale?.toLowerCase() === context.locale?.toLowerCase()
      && config.liquidLocale?.toLowerCase() === context.locale?.toLowerCase(), 'App locale mismatch');
    maisonRequire(config.country === context.country && config.marketCurrencyCode === context.currency
      && maisonInteger(config.marketId), 'App market mismatch');
    maisonRequire(Number.isFinite(config.currencyRate) && config.currencyRate > 0, 'Invalid currency context');
    maisonRequire(!config.abTestsRunning && !config.preview, 'Experiment or preview requires the native integration');
    maisonRequire(!(config.requireCustomerLogin && !config.isLoggedIn), 'Offer requires customer login');
    maisonRequire(typeof config.moneyFormat === 'string' && maisonObject(config.defaultTranslations), 'Invalid app presentation data');

    const productNodes = [...document.querySelectorAll('script.kaching-bundles-product[data-main]')]
      .filter((node) => maisonId(node.getAttribute('data-product-id')) === maisonId(context.product.id));
    maisonRequire(productNodes.length === 1, 'Missing or ambiguous target app product');
    const product = maisonJSON(productNodes[0]);
    maisonRequire(maisonObject(product) && maisonId(product.id) === maisonId(context.product.id)
      && product.handle === context.product.handle, 'App product identity mismatch');
    maisonRequire(Array.isArray(product.variants) && product.variants.length > 0
      && Array.isArray(context.product.variants) && product.variants.length === context.product.variants.length,
    'App variant set mismatch');
    const ids = new Set();
    for (const variant of product.variants) {
      const native = context.product.variants.find((item) => maisonId(item.id) === maisonId(variant.id));
      maisonRequire(native && maisonInteger(variant.id) && !ids.has(maisonId(variant.id)), 'App variant identity mismatch');
      ids.add(maisonId(variant.id));
      maisonRequire(typeof variant.availableForSale === 'boolean' && variant.availableForSale === native.available,
        'App availability mismatch');
      maisonRequire(Number.isSafeInteger(variant.price) && variant.price >= 0, 'Invalid app variant price');
      maisonRequire(Number.isSafeInteger(native.price) && variant.price === native.price, 'App variant price mismatch');
      maisonRequire(native.requiresComponents === false && product.isNativeBundle !== true, 'Native bundle requires the native integration');
    }
    maisonRequire(ids.has(maisonId(context.product.variantId))
      && maisonId(product.selectedVariantId) === maisonId(context.product.variantId), 'App selected variant mismatch');
    maisonRequire(!product.requiresSellingPlan, 'Subscription-only product requires the native integration');

    const dealNodes = [...document.querySelectorAll('script.kaching-bundles-deal-block-settings')]
      .filter((node) => maisonId(node.getAttribute('data-product-id')) === maisonId(product.id));
    const allDeals = dealNodes.map(maisonJSON).filter((deal) => deal !== null);
    maisonRequire(allDeals.every(maisonObject), 'Invalid app offer data');
    const eligible = allDeals.filter((deal) => maisonVisible(deal, product)
      && (!deal.marketId || maisonId(deal.marketId) === maisonId(config.marketId))
      && !(deal.excludedMarketIds || []).some((id) => maisonId(id) === maisonId(config.marketId))
      && !(config.b2bCustomer && deal.excludeB2bCustomers));
    maisonRequire(!eligible.some((deal) => deal.abTestVariantId), 'Offer experiment requires the native integration');
    // Native Kaching groups product, collection, exclusion and global targets
    // in that order, preserving market-specific priority within each group.
    // Ambiguous offers within that tier require the native selector rather
    // than relying on incidental JSON order.
    const visibility = ['selected-products', 'selected-collections', 'excluded-products', 'all-products'];
    const tier = visibility.map((type) => eligible.filter((deal) => deal.blockVisibility === type))
      .find((deals) => deals.length) || [];
    const scoped = tier.filter((deal) => deal.marketId);
    const candidates = scoped.length ? scoped : tier;
    maisonRequire(candidates.length === 1, 'Missing or ambiguous eligible offer');
    const dealBlock = candidates[0];
    maisonSimpleOffer(dealBlock, product);
    maisonRequire(!dealBlock.currency || dealBlock.currency.currencyCode === context.currency && config.currencyRate === 1,
      'Offer currency conversion requires the native integration');

    const translationNodes = document.querySelectorAll('script#kaching-bundles-translations');
    maisonRequire(translationNodes.length <= 1, 'Ambiguous app translations');
    const locales = translationNodes.length ? maisonJSON(translationNodes[0]) : [];
    maisonRequire(Array.isArray(locales), 'Invalid app translations');
    const locale = locales.find((entry) => entry?.locale === config.locale)?.translations;
    const translations = { ...config.defaultTranslations, ...locale?.system, ...locale?.dealBlocks?.[dealBlock.id] };
    maisonRequire(Object.values(translations).every((value) => typeof value === 'string'), 'Invalid translated copy');
    return { config, product, dealBlock, translations, otherProducts: [], mediaImages: [], collectionBreaksProducts: [] };
  }

  window.MaisonKachingData = Object.freeze({ read: maisonRead });
})();
